/* eslint-disable max-len */

import { useTranslation } from 'react-i18next';
import {
  capitalizeFirstLetterEveryword
} from '../../../shared/stringFunctions';
import { TXT_ADD_ALTERATION, TXT_ALTERATIONS } from '../../../translations/translationConstants';
import {
  TXT_SEARCH_BY_NAME
} from '../../../translations/translationConstants';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import {
  addAlterationInput,
  deleteAlterationInput,
  getAlterationsInput,
  getAlterationOutput,
  GET_ALTERATIONS,
  updateAlterationInput
} from '../../../api';
import { useEffect } from 'react';
import { useState } from 'react';
import { ManageAlterationsTable } from './ManageAlterationsTable';
import {
  DeleteConfirmation,
  ExportToExcel,
  globalPageSizeOptions,
  PaginationBar
} from '../../../components';
import { alteration } from '../../../entityModel';
import { useApolloErrorHandler, useAuthServices } from '../../../hooks';
import {
  ADD_ALTERATION,
  DELETE_ALTERATION,
  UPDATE_ALTERATION
} from '../../../api';
import { ManageAlterationEditor } from './ManagerAlterationEditor';

export const ManageAlterations = () => {
  const { t } = useTranslation();
  const { isSignedIn } = useAuthServices();

  const [txtSearch, setTxtSearch] = useState('');
  const [alterationData, setAlterationData] = useState<alteration[] | undefined>([]);

  const onTxtSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTxtSearch(event.currentTarget.value);
  };

  const [selectedAlteration, setSelectedAlteration] = useState<alteration | undefined>(undefined);
  const [getAlterations, { loading, data, error, refetch }] = useLazyQuery<getAlterationOutput>(GET_ALTERATIONS);
  const [getAllAlterations] = useLazyQuery<getAlterationOutput>(GET_ALTERATIONS, {
    onCompleted: (data) => setAlterationData(data.alterations.items)
  });

  useEffect(() => {
    getAlterations();
    getAllAlterations();
  }, []);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(globalPageSizeOptions[0]);

  const [openEditor, setOpenEditor] = useState(false);
  const [savingError, setSavingError] = useState<string | undefined>(undefined);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const lblSearch = capitalizeFirstLetterEveryword(t(TXT_SEARCH_BY_NAME));

  const fetchAlterations = () => {
    const variables: getAlterationsInput = {
      code: txtSearch,
      take: pageSize,
      skip: (pageNumber - 1) * pageSize
    };
    getAlterations({
      variables
    });
  };

  const fetch = (resetPage?: boolean) => {
    if (!!resetPage && pageNumber !== 1) {
      setPageNumber(1);
    } else {
      fetchAlterations();
    }
  };

  useEffect(() => {
    useApolloErrorHandler(isSignedIn, error);
  }, [error]);

  useEffect(() => {
    fetch();
  }, [pageNumber]);

  useEffect(() => {
    fetch(true);
  }, [pageSize, txtSearch]);

  const openEditAlterationEditor = (alteration: alteration) => {
    setSelectedAlteration(alteration);
    setOpenEditor(true);
  };

  const openDeleteAlterationConfirmation = (alteration: alteration) => {
    setSelectedAlteration(alteration);
    setOpenDeleteConfirmation(true);
  };

  const openAddAlterationEditor = () => {
    setSelectedAlteration(undefined);
    setOpenEditor(true);
  };

  const closeEditor = () => {
    setOpenEditor(false);
  };

  const afterAddingOrUpdatingAlteration = () => {
    setOpenEditor(false);
    if (refetch) {
      refetch();
    }
  };

  const handleAddingOrUpdatingAlterationError = (error: ApolloError) => {
    if (error.graphQLErrors.length > 0 &&
      !!error.graphQLErrors[0].extensions) {
      setSavingError(error.graphQLErrors[0].extensions['message']);
    } else {
      setSavingError(error.message);
    }
  };

  const [addAlteration, addAlterationOperation] = useMutation<alteration>(ADD_ALTERATION, {
    onError: (e) => handleAddingOrUpdatingAlterationError(e),
    onCompleted: (result) => afterAddingOrUpdatingAlteration()
  });

  const [updateAlteration, updateAlterationOperation] = useMutation<alteration>(UPDATE_ALTERATION, {
    onError: (e) => handleAddingOrUpdatingAlterationError(e),
    onCompleted: (result) => afterAddingOrUpdatingAlteration()
  });

  const onEditorSave = (payload: addAlterationInput | updateAlterationInput) => {
    if (!selectedAlteration) {
      addAlteration({
        variables: {
          input: payload
        }
      });
    } else {
      updateAlteration({
        variables: {
          input: payload
        }
      });
    }
  };

  const onDeleteCancel = () => setOpenDeleteConfirmation(false);

  const [deleteAlteration, deleteAlterationOperation] = useMutation<boolean>(DELETE_ALTERATION, {
    onError: (e) => { },
    onCompleted: (result) => {
      if (data && refetch) {
        if (data.alterations.items.length == 1) {
          const targetPage = pageNumber - 1 <= 0 ? 1 : pageNumber - 1;
          if (pageNumber !== targetPage) {
            setPageNumber(targetPage);
          } else {
            refetch();
          }
        } else {
          refetch();
        }
      }
      onDeleteCancel();
    }
  });

  const onDelete = () => {
    const input: deleteAlterationInput = {
      id: selectedAlteration!.id!
    };
    deleteAlteration({
      variables: {
        input
      }
    });
  };

  const onPageSizeChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSize(+event.target.value);
  };

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>{t(capitalizeFirstLetterEveryword(TXT_ALTERATIONS))}</h1>
            </div>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="container-fluid">
          {
            openEditor &&
            <ManageAlterationEditor
              alteration={selectedAlteration}
              onClose={closeEditor}
              onSave={onEditorSave}
              isSaving={
                !selectedAlteration ? addAlterationOperation.loading :
                  updateAlterationOperation.loading
              }
              savingError={savingError}
            />
          }
          {
            openDeleteConfirmation &&
            <DeleteConfirmation
              loading={deleteAlterationOperation.loading}
              onYes={onDelete}
              onNo={onDeleteCancel}
              objectName={selectedAlteration?.label || ''}
            />
          }
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className='row'>
                    <div className='col-6'>
                      <div className="form-group">
                        <label>{lblSearch}</label>
                        <input type='text' className='form-control' value={txtSearch} onChange={onTxtSearchChanged} />
                      </div>
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-md-6'>
                      <button
                        className='btn btn-primary'
                        data-target="#modal-alteration-editor"
                        data-toggle="modal"
                        disabled={loading}
                        onClick={openAddAlterationEditor}>
                        {t(TXT_ADD_ALTERATION).toUpperCase()}
                      </button>
                    </div>
                    <div className="col-md-6" style={{ textAlign: 'right' }}>
                      <ExportToExcel filename={TXT_ALTERATIONS} data={alterationData} />
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <ManageAlterationsTable
                    loading={loading}
                    data={data}
                    error={error}
                    onEdit={openEditAlterationEditor}
                    onDelete={openDeleteAlterationConfirmation}
                  />
                </div>
                <PaginationBar
                  currentPage={pageNumber}
                  totalCount={data?.alterations.totalCount}
                  pageSize={pageSize}
                  onPageChanged={(page) => setPageNumber(page)}
                  onChangeRowsPerPage={onPageSizeChanged}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
